/**
 * Returns an object to use with localforage setItems from an array of objects from server
 * @param {Array} array 
 */
const getObjFromArray = (array) => {
  return array.reduce((obj, item) => {
    if(item.dbKey) {
      obj[item.dbKey] = item;
    }
    return obj;
  }, {});
};

export default {
  // campi utilizzati come rootState di ogni applicazione della piattaforma
  rootState: {
    lang: 'it',
    workSelected: 'my',
    windowWidth: 0,
    windowHeight: 0,
    isDownloading: false,
    isSaving: false
  },
  rootGetters: {
    getWindowWidth: state => {
      return state.windowWidth;
    },
    getWindowHeight: state => {
      return state.windowHeight;
    },
    getWorkSelected: state => {
      return state.workSelected;
    },
  },
  rootMutations: {
    updateLang: (state, lang) => {
      state.lang = lang;
    },
    updateDimensions: (state, { width, height }) => {
      state.windowWidth = width;
      state.windowHeight = height;
    },
    setIsDownloading: (state, val) => {
      state.isDownloading = val;
    },
    setIsSaving: (state, val) => {
      state.isSaving = val;
    },
    setWorkSelected: (state, workId) => {
      state.workSelected = workId;
    },
  },
  rootActions: {
    updateLang: async ({ commit, state }, obj) => {
      commit('updateLang', obj.lang);
      obj.app.$moment.locale(state.lang);
    },
    updateDimensions: async ({ commit }, dimensions) => {
      commit('updateDimensions', dimensions);
    },
    setIsDownloading: async ({ commit }, val) => {
      commit("setIsDownloading", val);
    },
    setIsSaving: async ({ commit }, val) => {
      commit("setIsSaving", val);
    },
    setWorkSelected: async ({ commit }, idWorldWork) => {
      commit("setWorkSelected", idWorldWork);
    },
  },
  // Metodi per generari i moduli dello store
  createDefaultGetters() {
    return {
      get: state => id => {
        return state.objs.find(obj => obj.dbKey == id);
      },
      getAll: state => {
        return state.objs;
      }
    }
  },
  createDefaultMutations() {
    return {
      update: (state, objs) => {
        const newState = state.objs.slice(0);
        objs.forEach(obj => {
          const index = newState.findIndex(o => o.dbKey == obj.dbKey);
          if (-1 !== index) {
            newState.splice(index, 1, obj);
          } else {
            newState.push(obj);
          }
        });
        state.objs = newState;
      },
      deleteMultiple: (state, ids) => {
        let newState = state.objs.slice(0);
        ids.forEach(id => {
          let index = newState.findIndex(obj => obj.dbKey == id);
          if (-1 !== index) {
            newState.splice(index, 1);
          }
        });
        state.objs = newState;
      },
      delete: (state, id) => {
        let index = state.objs.findIndex(obj => obj.dbKey == id);
        if (-1 !== index) {
          state.objs.splice(index, 1);
        }
      }
    }
  },
  createDefaultActions() {
    return {
      update: async ({ commit }, objs) => {
        commit('update', objs);
      },
      delete: async ({ commit }, id) => {
        commit("delete", id);
      }
    }
  },
  createDefaultActionsWithStore() {
    return {
      /**
       * Given a localforage instance with the correct store, 
       * updates app state with its contents
       */
      load: async ({ commit, state, rootState }, vlf ) => {
        if(!state.objs || state.objs.length === 0) {
          const array = [];
          await vlf.iterate((val, key, index) => {
            array.push(val);
          });
          commit('update', array);
        } else if(rootState.offline.isOffline) {
          // Se offline, chiamata non bloccante in caso di store già popolato
          const array = [];
          vlf.iterate((val, key, index) => {
            array.push(val);
          })
          .then(result => commit('update',array));
        }
      },
      /**
       * Given an array of objects from the server, updates app state
       * and then saves to the local db (IndexedDB).
       * Optionally, an array of functions can be passed to clean local db
       * and app state of removed elements (the 'clean' array must be empty to clear all).
       * Steps: 
       *  - clear elements in app state based on server's response and conditions given
       *  - updates elements in app state
       *  - save changes to local storage (IndexedDB)
       */
      update: async ({ commit, state, dispatch }, { objs, vlf, saveFilters, cleanFilters }) => {
        if(objs) {
          objs = objs.filter(o => o);
          /* Filter null/empty objects */
          const saveToIdb = saveFilters 
            ? objs.filter(o => saveFilters.reduce((result, temp) => result && temp(o), true)) 
            : objs;
          const toRemove = [];
          /* Update state */
          if(cleanFilters) {
            const idsFromServer = objs.map(o => o.dbKey);
            state.objs.forEach(stateObj => {
              const sid = stateObj.dbKey;
              const condition = cleanFilters.reduce((result, temp) => result && temp(stateObj), true);
              // Elemento da rimuovere dallo stato e dal db locale
              if(!idsFromServer.includes(sid) && condition) {
                toRemove.push(sid);
              }
            });
            commit('deleteMultiple', toRemove);
          }
          commit('update', Object.values(objs));
          dispatch('setIsSaving', true, { root: true });
          // Fire and forget
          Promise.all(toRemove.map(id => vlf.removeItem(id)))
            .then(() => {vlf.setItems(getObjFromArray(saveToIdb))})
            .catch(e => console.log(e))
            .finally(() => {dispatch('setIsSaving', false, { root: true })});
        }
      },
      /**
       * Given an object, deletes it from app state and then local db.
       */
      delete: async ({ commit, dispatch }, { vlf, obj }) => {
        commit("delete", obj.dbKey);
        dispatch('setIsSaving', true, { root: true });
        vlf.removeItem(obj.dbKey)
          .catch(e => console.log(e))
          .finally(() => dispatch('setIsSaving', false, { root: true }));
      },
      /**
       * Given an array of objects, deletes them from app state and then local db.
       */
      deleteMultiple: async ({ commit, dispatch }, { vlf, objs }) => {
        commit('deleteMultiple', objs.map(o => o.dbKey));
        dispatch('setIsSaving', true, { root: true });
        Promise.all(objs.map(o => vlf.removeItem(o.dbKey)))
          .catch(e => console.log(e))
          .finally(() => dispatch('setIsSaving', false, { root: true }));
      }
    }
  }
}
